import { useCallback, useRef, useState } from "react";
import { useTranslator } from "@sia/style-guide";
import surveyAdapter from "common/Adapters/SurveyAdapter/Router";
import { CryptoInitializationError } from "common/crypto";
import { createFailedEmployee, formatInvalidEmployees, getEmployeeId, hasEmployeeErrors, isSameEmployee, upsertEmployeeInList } from "./utils";

const UI_SCOPE_CHANGED = "EMPLOYEE_UI_SCOPE_CHANGED";
const positiveInt = (value, fallback) => {
  const number = Number.parseInt(value, 10);
  return Number.isInteger(number) && number > 0 ? number : fallback;
};
const wait = milliseconds => (milliseconds > 0 ? new Promise(resolve => setTimeout(resolve, milliseconds)) : Promise.resolve());
const listError = message => [{ content: message, children: [] }];
const scopeChangedError = () => Object.assign(new Error("Employee import scope changed while the import was running."), { code: UI_SCOPE_CHANGED });

export function useEmployeeImport({
  employeesRef,
  replaceEmployees,
  persistEmployee,
  isMutationBlocked,
  onRequiresReload,
  cryptoSessionRevision,
  getUiScope,
}) {
  const translate = useTranslator();
  const [isImportingEmployees, setIsImportingEmployees] = useState(false);
  const operationRef = useRef(null);
  const isImportActive = useCallback(() => Boolean(operationRef.current), []);

  const importEmployees = useCallback(employees => {
    if (operationRef.current) return operationRef.current;
    const importCryptoRevision = cryptoSessionRevision;
    const importUiScope = getUiScope();
    const assertImportScope = () => {
      surveyAdapter.assertCryptoSessionRevision(importCryptoRevision);
      if (getUiScope() !== importUiScope) throw scopeChangedError();
    };
    const operation = (async () => {
      if (isMutationBlocked()) return { ok: false, errors: listError("Employee changes are blocked until the survey is reloaded.") };
      if (!Array.isArray(employees) || !employees.length) return { ok: false, errors: listError("There are no employees to import.") };
      setIsImportingEmployees(true);
      assertImportScope();

      const existingIds = employeesRef.current.map(getEmployeeId).filter(id => id != null);
      if (existingIds.length) {
        const deletion = await surveyAdapter.deleteEmployeeInBulk(existingIds, importCryptoRevision);
        assertImportScope();
        const deletedIds = deletion?.deletedIds || [];
        if (deletion?.ok !== true) {
          if (deletedIds.length) replaceEmployees(current => current.filter(employee => !deletedIds.some(id => isSameEmployee(employee, id))));
          const count = deletion?.failures?.length || 1;
          return {
            ok: false,
            errors: listError(`${count} existing employee${count === 1 ? "" : "s"} could not be removed. The import was stopped.`),
            importedCount: 0,
            failedCount: count,
          };
        }
      }

      assertImportScope();
      replaceEmployees(() => []);
      const failures = [];
      let importedCount = 0;
      let requiresReload = false;
      const chunkSize = positiveInt(process.env.REACT_APP_SUBMIT_CHUNK_SIZE || process.env.REACT_APP_SUBMIT_CHNUK_SIZE, employees.length);
      const interval = positiveInt(process.env.REACT_APP_SUBMIT_INTERVAL, 0);

      for (let index = 0; index < employees.length; index += 1) {
        assertImportScope();
        const input = employees[index];
        const result = await persistEmployee(input, importCryptoRevision, importUiScope);
        assertImportScope();
        const saved = result?.employee || input;
        if (!result?.ok || hasEmployeeErrors(saved)) {
          failures.push(saved);
          requiresReload = Boolean(result?.requiresReload);
          if (requiresReload) {
            onRequiresReload(importUiScope);
            break;
          }
        } else {
          try {
            assertImportScope();
            replaceEmployees(current => upsertEmployeeInList(current, saved));
            importedCount += 1;
          } catch (error) {
            failures.push({ ...createFailedEmployee(saved, error), persistenceUncertain: true });
            requiresReload = true;
            onRequiresReload(importUiScope);
            break;
          }
        }
        if ((index + 1) % chunkSize === 0 && index < employees.length - 1) {
          await wait(interval);
          assertImportScope();
        }
      }

      return {
        ok: failures.length === 0,
        errors: formatInvalidEmployees(failures, translate),
        importedCount,
        failedCount: failures.length,
        requiresReload,
      };
    })()
      .catch(error => {
        const scopeChanged = error?.code === UI_SCOPE_CHANGED || getUiScope() !== importUiScope;
        const requiresReload = !scopeChanged && error instanceof CryptoInitializationError;
        if (requiresReload) onRequiresReload(importUiScope);
        return {
          ok: false,
          errors: listError(error?.response?.data?.message || error?.message || "Employee import failed."),
          error,
          requiresReload,
        };
      })
      .finally(() => {
        operationRef.current = null;
        setIsImportingEmployees(false);
      });

    operationRef.current = operation;
    return operation;
  }, [cryptoSessionRevision, employeesRef, getUiScope, isMutationBlocked, onRequiresReload, persistEmployee, replaceEmployees, translate]);

  return { isImportingEmployees, isImportActive, importEmployees };
}
